import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useWarranties } from '@/hooks/useWarranties';
import WarrantyModal from './components/WarrantyModal';
import { updateWarrantyStatus } from '@/services/warranties';
import { useAuth } from '@/context/AuthContext';
import { isOperationsRole } from '@/lib/rbac';

const REVIEW_MAP: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pendiente', color: 'text-amber-600', bg: 'bg-amber-50' },
  under_review: { label: 'En revisión', color: 'text-cyan-600', bg: 'bg-cyan-50' },
  approved: { label: 'Aprobada', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  rejected: { label: 'Rechazada', color: 'text-red-600', bg: 'bg-red-50' },
  delivered: { label: 'Entregada', color: 'text-emerald-700', bg: 'bg-emerald-100' },
};

const RESOLUTION_LABEL: Record<string, string> = {
  replace: 'Reemplazo',
  refund: 'Reembolso',
  repair: 'Reparación',
  rejected: 'Rechazada',
};

const REVIEW_STATUSES = ['all', 'pending', 'under_review', 'approved', 'rejected', 'delivered'];
const REVIEW_LABELS: Record<string, string> = {
  all: 'Todas',
  pending: 'Pendientes',
  under_review: 'En revisión',
  approved: 'Aprobadas',
  rejected: 'Rechazadas',
  delivered: 'Entregadas',
};

export default function WarrantiesPage() {
  const { profile } = useAuth();
  const isOperations = isOperationsRole(profile?.role ?? '');
  const [filter, setFilter] = useState('all');
  const { data: warranties, loading, error, refetch } = useWarranties();
  const [showNew, setShowNew] = useState(false);
  const [actionError, setActionError] = useState('');

  async function handleStatusChange(id: string, status: string) {
    setActionError('');
    const { error: updateError } = await updateWarrantyStatus(id, status);
    if (updateError) setActionError(updateError);
    refetch();
  }

  const filtered = warranties.filter((w) => filter === 'all' || w.reviewStatus === filter);

  const counts: Record<string, number> = {};
  REVIEW_STATUSES.forEach((s) => {
    counts[s] = s === 'all' ? warranties.length : warranties.filter((w) => w.reviewStatus === s).length;
  });

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        {actionError && (
          <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
            <i className="ri-error-warning-line"></i>
            <span>{actionError}</span>
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Garantías</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{warranties.length} reclamos de garantía</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          {!isOperations && (
            <button
              onClick={() => setShowNew(true)}
              className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-add-line mr-1.5"></i>Nuevo Reclamo
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Total Reclamos</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{warranties.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">En Revisión</p>
            <p className="text-xl font-bold text-cyan-600 mt-1">{counts.under_review || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Aprobadas</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">{counts.approved || 0}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Rechazadas</p>
            <p className="text-xl font-bold text-red-600 mt-1">{counts.rejected || 0}</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {REVIEW_STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors whitespace-nowrap cursor-pointer ${
                filter === s ? 'bg-accent-500 text-white' : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {REVIEW_LABELS[s]} ({counts[s] || 0})
            </button>
          ))}
        </div>

        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Garantía</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Cliente</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden xl:table-cell">Vendedor</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden md:table-cell">Producto</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Motivo</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden lg:table-cell">Vigencia</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Revisión</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden sm:table-cell">Resolución</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((w) => {
                  const rv = REVIEW_MAP[w.reviewStatus] || { label: w.reviewStatus, color: 'text-foreground-500', bg: 'bg-background-100' };
                  return (
                    <tr key={w.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3.5 text-accent-600 font-medium whitespace-nowrap">{w.warrantyNumber}</td>
                      <td className="px-5 py-3.5">
                        <div>
                          <p className="text-foreground-900 font-medium text-sm">{w.customerName || '—'}</p>
                          {w.customerCity && <p className="text-xs text-foreground-500">{w.customerCity}</p>}
                        </div>
                      </td>
                      <td className="px-5 py-3.5 hidden xl:table-cell text-foreground-700">{w.salespersonName || '—'}</td>
                      <td className="px-5 py-3.5 hidden md:table-cell">
                        <div>
                          <p className="text-foreground-700 text-sm">{w.productName || '—'}</p>
                          {w.sizeLabel && <p className="text-xs text-foreground-400">{w.sizeLabel}</p>}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <p className="text-foreground-700 text-sm max-w-[220px] truncate">{w.reportedReason}</p>
                      </td>
                      <td className="px-5 py-3.5 hidden lg:table-cell">
                        <div>
                          <p className="text-xs text-foreground-700">{w.warrantyPeriod} días</p>
                          <p className="text-xs text-foreground-400 whitespace-nowrap">
                            Vence {w.expiresAt ? new Date(w.expiresAt).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' }) : '—'}
                          </p>
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <select
                          value={w.reviewStatus}
                          onChange={(e) => handleStatusChange(w.id, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${rv.color} ${rv.bg}`}
                        >
                          {REVIEW_STATUSES.filter((s) => s !== 'all').map((s) => (
                            <option key={s} value={s}>{REVIEW_LABELS[s]}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3.5 hidden sm:table-cell">
                        {w.resolutionType ? (
                          <span className="text-xs text-foreground-700">{RESOLUTION_LABEL[w.resolutionType] || w.resolutionType}</span>
                        ) : (
                          <span className="text-xs text-foreground-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-inbox-line text-3xl block mb-2"></i>
              <p className="text-sm">No se encontraron reclamos de garantía</p>
            </div>
          )}
          {loading && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-2xl block mb-2"></i>
              <p className="text-sm">Cargando reclamos...</p>
            </div>
          )}
        </div>
      </div>

      {showNew && <WarrantyModal onClose={() => setShowNew(false)} onSaved={refetch} />}
    </AdminLayout>
  );
}